import { selectAllExperiences } from '../../shared/upload-document/redux/selectors/experiences.selector';
import { catchError, filter, map, switchMap, tap } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { defer, Observable } from 'rxjs';
import { Experience, ExperiencesService, Order } from '@cemex-core/cmx-api';
import { ACTION_TYPES, ORDER_ACTIONS } from '@ordersnproducts/modules/shared/constants/order-actions';
import { of } from 'rxjs';
import { ORDER_EXPERIENCES } from '@ordersnproducts/modules/shared/constants/order-experiences';
import { _safe } from '@ordersnproducts/modules/shared/utils/safe-get.method';
import { SetExperience } from '@ordersnproducts/modules/order-builder/modules/location/actions/experiences-actions/experiences.actions';

export function getExperienceByName(store: Store<any>, featureName: string): Observable<any> {
  return store.select(selectAllExperiences).pipe(
    filter((experiences) => {
      return !!experiences;
    }),
    map((experiences) => {
      const tmpFeatures = [];
      for (let i = 0; i < experiences.length; i++) {
        for (let j = 0; j < experiences[i].features.length; j++) {
          tmpFeatures.push(experiences[i].features[j]);
        }
      }
      return tmpFeatures;
    }),
    map((features) => {
      const expExists = features.find((feature) => feature.key === featureName);
      return expExists;
    }),
  );
}

export function getExperienceCode(store: Store<any>, experienceCode: ORDER_EXPERIENCES): Observable<any> {
  return store.select(selectAllExperiences).pipe(
    filter((experiences) => {
      return experiences?.length > 0;
    }),
    map(
      (experiences) => experiences.find((experience) => experience?.experienceCode === experienceCode)?.experienceCode,
    ),
  );
}

/**
 * Get the experience from the API
 * @param experiencesApi service to get experiences from the API
 * @param jobsiteCode jobsite code to get the experience
 * @param productTypeCode product type code to get the experience
 * @param actionCode action code to get the experience
 * @returns experience from the API and undefined if there is an error
 */
export function getExperience(
  experiencesApi: ExperiencesService,
  jobsiteCode: string,
  productTypeCode: string,
  actionCode: ORDER_ACTIONS,
): Observable<Experience> {
  const requestParams = {
    jobsiteCode,
    productTypeCode: productTypeCode,
    actionType: ACTION_TYPES.ORDER,
    actionCode: actionCode,
  };

  return experiencesApi.getExperiences(requestParams).pipe(
    map((experiencesResponse) => {
      return experiencesResponse.experiences[0];
    }),
    catchError((error) => {
      console.error('Error getting experiences', error);
      return of(undefined);
    }),
  );
}

/**
 * Load the experience from the API and dispatch the experience when the experience is COMMAND_UNIVERSAL_LINK
 * @param experiencesApi service to get experiences from the API
 * @param store get order from the store and dispatch the experience
 * @param actionCode action code to get the experience
 * @param selectedExperience experience selected
 * @param order order selected to get the information
 * @param getSelectedOrder$ determine when the order data is available
 * @returns
 */
export function loadExperience(
  experiencesApi: ExperiencesService,
  store: Store<any>,
  actionCode: ORDER_ACTIONS,
  selectedExperience: string,
  order: Order,
  getSelectedOrder$: Observable<Order>,
) {
  const getExperience$ = defer(() => {
    if (selectedExperience !== ORDER_EXPERIENCES.COMMAND_UNIVERSAL_LINK) {
      return of(undefined);
    }
    const jobsiteCode = order.jobsite.jobsiteCode;
    if (!order.orderProducts) {
      return getSelectedOrder$.pipe(
        switchMap((orderDetail) => {
          const _productTypeCode = orderDetail.orderProducts[0].productType.productTypeCode;
          return getExperience(experiencesApi, jobsiteCode, _productTypeCode, actionCode).pipe(
            map((jobsiteExperience) => _safe(() => jobsiteExperience.features, undefined)),
          );
        }),
      );
    }
    const productTypeCode = order.orderProducts[0].productType.productTypeCode;

    return getExperience(experiencesApi, jobsiteCode, productTypeCode, ORDER_ACTIONS.MODIFY).pipe(
      map((jobsiteExperience) => _safe(() => jobsiteExperience.features, undefined)),
    );
  }).pipe(
    tap((features) => {
      store.dispatch(
        new SetExperience({
          _links: [],
          experienceId: 0,
          isOnline: 0,
          experienceCode: selectedExperience,
          features,
        }),
      );
    }),
  );
  return getExperience$;
}
